import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FaWallet, FaChartPie, FaTrophy, FaCoins, FaArrowUp, FaCalendar, FaDollarSign } from 'react-icons/fa';
import axios from 'axios';
import { useAutoSwitchNetwork } from '../hooks/useAutoSwitchNetwork';
import './Dashboard.css';

const Dashboard = () => {
  useAutoSwitchNetwork(); // Auto-switch to Avalanche
  const [treasuryData, setTreasuryData] = useState(null);
  const [payoutsData, setPayoutsData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Treasury Dashboard | Velvet AI';
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      
      const [treasuryResponse, payoutsResponse] = await Promise.all([
        axios.get(`${apiUrl}/portfolio/treasury-balance`),
        axios.get(`${apiUrl}/portfolio/payouts`)
      ]);

      setTreasuryData(treasuryResponse.data);
      setPayoutsData(payoutsResponse.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Set fallback data for development/testing
      setTreasuryData({
        startingBalance: 750,
        currentBalance: 1245.67,
        unrealizedProfit: 125.34,
        avaxPrice: 42.15,
        timestamp: new Date().toISOString()
      });
      setPayoutsData({
        payouts: [
          { date: '2025-03-04', amount: 140, valueUSD: 3395, avaxPrice: 24.25 },
          { date: '2025-05-15', amount: 160, valueUSD: 3880, avaxPrice: 24.25 },
          { date: '2025-06-19', amount: 320, valueUSD: 7760, avaxPrice: 24.25 }
        ],
        totalAVAXDistributed: 620,
        currentAVAXPrice: 42.15,
        timestamp: new Date().toISOString()
      });
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    const number = typeof num === 'number' ? num : parseFloat(num) || 0;
    return number.toLocaleString('en-US', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
  };

  const formatCurrency = (num) => {
    const number = typeof num === 'number' ? num : parseFloat(num) || 0;
    return `$${number.toLocaleString('en-US', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  const currentBalance = treasuryData?.currentBalance || 0;
  const startingBalance = treasuryData?.startingBalance || 750;
  const avaxPrice = treasuryData?.avaxPrice || payoutsData?.currentAVAXPrice || 0;
  const totalDistributed = payoutsData?.totalAVAXDistributed || 0;
  const gainsAboveInitial = currentBalance - startingBalance;
  const gainsPercentage = ((gainsAboveInitial / startingBalance) * 100);
  
  // Calculate total AVAX earned: distributed + (current - initial)
  const totalAVAXEarned = totalDistributed + gainsAboveInitial;
  const totalEarnedPercentage = ((totalAVAXEarned / startingBalance) * 100);

  return (
    <div className="dashboard">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="page-title">Treasury Dashboard</h1>
        
        {/* Key Metrics Cards */}
        <div className="dashboard-stats">
          <div className="stat-card primary-stat">
            <div className="stat-icon">
              <FaTrophy />
            </div>
            <div className="stat-content">
              <div className="stat-label">Total AVAX Earned</div>
              <div className="stat-value">{formatNumber(totalAVAXEarned)} AVAX</div>
              <div className="stat-change">+{totalEarnedPercentage.toFixed(2)}%</div>
              <div className="stat-subtext">{formatCurrency(totalAVAXEarned * avaxPrice)}</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <FaWallet />
            </div>
            <div className="stat-content">
              <div className="stat-label">Current Treasury Balance</div>
              <div className="stat-value">{formatNumber(currentBalance)} AVAX</div>
              <div className="stat-subtext">{formatCurrency(currentBalance * avaxPrice)}</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <FaChartPie />
            </div>
            <div className="stat-content">
              <div className="stat-label">Initial Treasury Balance</div>
              <div className="stat-value">{formatNumber(startingBalance)} AVAX</div>
              <div className="stat-subtext">{formatCurrency(startingBalance * avaxPrice)}</div>
            </div>
          </div>

        </div>

        <div className="dashboard-content">
          {/* Distributions Table */}
          <section className="distributions-section">
            <h2 className="section-header">
              <FaDollarSign /> Distribution History
            </h2>
            <div className="distributions-table glass-card">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Amount (AVAX)</th>
                    <th>Value (USD)</th>
                  </tr>
                </thead>
                <tbody>
                  {payoutsData?.payouts?.map((payout, index) => (
                    <motion.tr
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                    >
                      <td>
                        <div className="date-cell">
                          <FaCalendar className="date-icon" />
                          {formatDate(payout.date)}
                        </div>
                      </td>
                      <td className="amount-cell">{formatNumber(payout.amount)} AVAX</td>
                      <td className="value-cell">{formatCurrency(payout.valueUSD)}</td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Summary Cards */}
          <div className="summary-grid">
            <div className="summary-card glass-card">
              <div className="summary-icon">
                <FaTrophy />
              </div>
              <div className="summary-content">
                <div className="summary-label">Total AVAX Distributed</div>
                <div className="summary-value">{formatNumber(totalDistributed)} AVAX</div>
                <div className="summary-subtext">
                  {formatCurrency(totalDistributed * avaxPrice)} at current price
                </div>
              </div>
            </div>

            <div className="summary-card glass-card">
              <div className="summary-icon">
                <FaCoins />
              </div>
              <div className="summary-content">
                <div className="summary-label">Current AVAX Price</div>
                <div className="summary-value">{formatCurrency(avaxPrice)}</div>
                <div className="summary-subtext">
                  Live market price
                </div>
              </div>
            </div>

            <div className="summary-card glass-card">
              <div className="summary-icon">
                <FaArrowUp />
              </div>
              <div className="summary-content">
                <div className="summary-label">Treasury Growth</div>
                <div className="summary-value">+{gainsPercentage.toFixed(1)}%</div>
                <div className="summary-subtext">
                  Since inception ({formatNumber(gainsAboveInitial)} AVAX)
                </div>
              </div>
            </div>
          </div>

          {/* Performance Metrics */}
          <section className="performance-section">
            <h2 className="section-header">
              <FaChartPie /> Treasury Performance
            </h2>
            <div className="performance-grid">
              <div className="performance-card glass-card">
                <div className="performance-label">Starting Balance</div>
                <div className="performance-value">{formatNumber(startingBalance)} AVAX</div>
                <div className="performance-usd">{formatCurrency(startingBalance * avaxPrice)}</div>
              </div>
              
              <div className="performance-card glass-card">
                <div className="performance-label">Current Balance</div>
                <div className="performance-value">{formatNumber(currentBalance)} AVAX</div>
                <div className="performance-usd">{formatCurrency(currentBalance * avaxPrice)}</div>
              </div>
              
              <div className="performance-card glass-card">
                <div className="performance-label">Total Distributed</div>
                <div className="performance-value">{formatNumber(totalDistributed)} AVAX</div>
                <div className="performance-usd">{formatCurrency(totalDistributed * avaxPrice)}</div>
              </div>
              
              <div className="performance-card glass-card">
                <div className="performance-label">Net Growth</div>
                <div className="performance-value positive">+{totalEarnedPercentage.toFixed(1)}%</div>
                <div className="performance-usd">{formatCurrency(totalAVAXEarned * avaxPrice)}</div>
              </div>
            </div>
          </section>
        </div>

        <div className="dashboard-footer">
          <p className="last-updated">
            Last updated: {treasuryData?.timestamp ? new Date(treasuryData.timestamp).toLocaleString() : 'N/A'}
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default Dashboard;